import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import logoContrato from '@/assets/logo-contrato.png';
import { Botao } from '@/componentes/Botao';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { formatarData } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';

import { apiDaFolha } from './folhas';

/**
 * A FOLHA IMPRESSA (decisão 9): uma máquina, um tanque, um período — o que é
 * impresso ninguém precisa escrever. O código vai grande no topo, porque é por
 * ele que a leitura acha a folha, e em fonte de conferência. A tabela tem
 * linhas numeradas e largas o bastante para a letra de quem está no campo.
 *
 * Fora da casca, como o login: é uma página para o papel, sem menu. O botão
 * de imprimir some na impressão.
 *
 * O desenho é o da folha impressa da v2.1 (p.43): o logotipo e o código no
 * topo, os dados da folha em rótulo e valor, só preto e cinza, e o rodapé com
 * a co-marca dos documentos (p.61). As caixas de algarismo e o QR da p.43
 * mudam o que a IA lê do papel, e entram com a leitura.
 */

const rota = getRouteApi('/impressao/folha/$id');
const LINHAS = 20;
const dataCurta = (iso: string) => iso.split('-').reverse().join('/');

export function PaginaFolhaImpressa() {
  const { id } = rota.useParams();
  const folha = useQuery({ queryKey: ['combustivel', 'folhas', 'folha', id], queryFn: () => apiDaFolha.folha(id) });

  if (folha.isError || (!folha.isPending && !folha.data)) {
    return (
      <div className="p-8">
        <EstadoVazio
          titulo="Não foi possível abrir a folha"
          descricao={folha.isError ? explicarFalha(folha.error).comoResolver : 'Abra a folha pela lista de folhas de campo.'}
        />
      </div>
    );
  }
  /* `null` enquanto carrega (p.32): a folha aparece inteira desde o primeiro
     quadro — logotipo, rótulos, a grade de vinte linhas —, e só o que vem da
     resposta é barra. O botão de imprimir também: imprimir agora sairia sem
     máquina e sem código. */
  const f = folha.data?.folha ?? null;
  const medidor = f?.tipoMedidor === 'odometro' ? 'Odômetro (km)' : 'Horímetro (h)';

  return (
    <div className="h-full overflow-auto bg-card print:overflow-visible">
      <QuadroDaFolha carregando={f === null}>
        <div className="flex justify-end gap-2 print:hidden">
          {f === null ? (
            <Barra className="h-[var(--controle-m)] w-36 rounded-controle" />
          ) : (
            <Botao
              nivel="primario"
              onClick={() => {
                window.print();
              }}
            >
              Imprimir folha
            </Botao>
          )}
        </div>

        <header className="flex items-start justify-between gap-4 border-b-2 border-tinta pb-3">
          <img src={logoContrato} alt="Contrato Terraplanagem" width={68} height={56} className="h-14 w-auto" />
          <div className="flex flex-col items-end gap-1">
            <span className="txt-secao">Folha de abastecimento</span>
            {/* O código é por onde a leitura acha a folha: grande e em mono. */}
            {f === null ? (
              <Barra className="my-1 h-5 w-32" />
            ) : (
              <span className="txt-secao">
                <span className="mono">{f.codigo}</span>
              </span>
            )}
          </div>
        </header>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-2">
          {(
            [
              ['Máquina', f && `${f.equipamentoCodigo} · ${f.equipamentoDescricao}`],
              ['Tanque ou comboio', f && `${f.tanqueCodigo} · ${f.tanqueNome} · ${f.produtoNome}`],
              ['Período', f && `${dataCurta(f.de)} a ${dataCurta(f.ate)}`],
              ['Empresa', f?.empresa ?? null],
            ] as const
          ).map(([rotulo, valor], i) => (
            <div key={rotulo} className="flex flex-col">
              <dt className="txt-legenda font-normal text-tinta-secundaria">{rotulo}</dt>
              <dd className="txt-corpo font-semibold">
                {valor ?? <Barra className={`my-1 h-3 ${i % 2 === 0 ? 'w-3/4' : 'w-1/2'}`} />}
              </dd>
            </div>
          ))}
        </dl>

        <div className="flex flex-wrap items-center gap-6">
          <span className="txt-corpo">Preenchida com:</span>
          <span className="txt-corpo">☐ Caneta</span>
          <span className="txt-corpo">☐ Lápis</span>
          <span className="txt-apoio">Marque um. Escreva os números com traço firme; se errar, risque uma vez e escreva ao lado.</span>
        </div>

        <table className="w-full border-collapse">
          <thead>
            <tr className="txt-legenda">
              <th className="w-12 border border-tinta p-2 text-left">Nº</th>
              <th className="w-24 border border-tinta p-2 text-left">Data (DD/MM)</th>
              <th className="w-20 border border-tinta p-2 text-left">Hora</th>
              <th className="border border-tinta p-2 text-right">
                {f === null ? <Barra className="my-1 ml-auto h-2 w-24" /> : medidor}
              </th>
              <th className="w-24 border border-tinta p-2 text-right">Litros</th>
              <th className="border border-tinta p-2 text-left">Operador</th>
              <th className="w-20 border border-tinta p-2 text-left">Visto</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: LINHAS }, (_, i) => (
              <tr key={i} className="h-10">
                <td className="txt-corpo border border-tinta p-2 tabular-nums">{i + 1}</td>
                <td className="border border-tinta" />
                <td className="border border-tinta" />
                <td className="border border-tinta" />
                <td className="border border-tinta" />
                <td className="border border-tinta" />
                <td className="border border-tinta" />
              </tr>
            ))}
          </tbody>
        </table>

        {f === null ? (
          <span className="flex flex-col gap-2 py-1">
            <Barra className="h-3 w-full" />
            <Barra className="h-3 w-3/5" />
          </span>
        ) : (
          <p className="txt-apoio">
            {`Esta folha vale só para a máquina ${f.equipamentoCodigo} e o tanque ${f.tanqueCodigo}, no período impresso. Ao terminar, entregue para escanear: a leitura é conferida por quem escaneia antes de entrar no sistema.`}
          </p>
        )}

        {/* O rodapé dos documentos (p.61): a Contrato e, assinando, a Atenas. */}
        <footer className="txt-legenda flex flex-wrap justify-between gap-2 border-t border-borda pt-2 font-normal text-tinta-secundaria">
          <span>Contrato Terraplanagem · Sistema desenvolvido pela Atenas</span>
          {f === null ? (
            <Barra className="my-1 h-2 w-32" />
          ) : (
            <span>{`Emitida em ${formatarData(new Date(f.emitidaEm))}`}</span>
          )}
        </footer>
      </QuadroDaFolha>
    </div>
  );
}

/** O papel. Carregando, ele é a região que diz ao leitor de tela o que vem. */
function QuadroDaFolha({ carregando, children }: { readonly carregando: boolean; readonly children: ReactNode }) {
  const classe = 'mx-auto flex max-w-[var(--folha-largura)] flex-col gap-4 p-8 text-tinta print:max-w-none print:p-0';
  return carregando ? (
    <Carregando oQue="Carregando a folha" className={classe}>
      {children}
    </Carregando>
  ) : (
    <div className={classe}>{children}</div>
  );
}
